import { FormField } from '../types/form';
import { isSelectFieldType } from '../types/guards';

export const initializeFieldOptions = (field: FormField): FormField => {
  if (!shouldInitializeOptions(field)) {
    return field;
  }

  switch (field.field_type) {
    case 'dropdown':
    case 'radio':
    case 'checkbox':
      return initializeSelectFieldOptions(field);
    
    case 'checkbox_with_input':
      return initializeCheckboxWithInputOptions(field);
    
    case 'logic':
      return initializeLogicFieldOptions(field);
    
    case 'multi_row':
      return initializeMultiRowFieldOptions(field);

    case 'av_check':
      return initializeAVCheckFieldOptions(field);

    case 'connection_check':
      return initializeConnectionCheckFieldOptions(field);

    case 'scheduler':
      return initializeSchedulerFieldOptions(field);

    default:
      return field;
  }
};

const shouldInitializeOptions = (field: FormField): boolean => {
  return isSelectFieldType(field.field_type) ||
         field.field_type === 'checkbox_with_input' ||
         field.field_type === 'logic' ||
         field.field_type === 'multi_row' ||
         field.field_type === 'av_check' ||
         field.field_type === 'connection_check' ||
         field.field_type === 'scheduler';
};

const initializeSelectFieldOptions = (field: FormField): FormField => {
  if (!field.options || typeof field.options !== 'object') {
    return { ...field, options: [] };
  }

  if (Array.isArray(field.options)) {
    return field;
  }

  const optionsArray = Object.entries(field.options).map(([key, value]) => 
    typeof value === 'string' ? value : key
  );

  return { ...field, options: optionsArray };
};

const initializeCheckboxWithInputOptions = (field: FormField): FormField => {
  if (!field.options || typeof field.options !== 'object') {
    return {
      ...field,
      options: {
        inputType: 'text',
        inputPlaceholder: 'Enter value',
        customOptions: []
      }
    };
  }

  const options = field.options as any;
  const { inputType, inputPlaceholder, ...checkboxOptions } = options;
  
  const optionsArray = Object.entries(checkboxOptions).map(([key, value]) => 
    typeof value === 'string' ? value : key
  );

  return {
    ...field,
    options: {
      inputType: inputType || 'text',
      inputPlaceholder: inputPlaceholder || 'Enter value',
      customOptions: optionsArray.length > 0 ? optionsArray : []
    }
  };
};

const initializeLogicFieldOptions = (field: FormField): FormField => {
  if (!field.options || typeof field.options !== 'object') {
    return {
      ...field,
      options: {
        logicType: 'sum_checkbox_inputs',
        sourceFieldId: ''
      }
    };
  }

  const options = field.options as any;
  
  return {
    ...field,
    options: {
      logicType: options.logicType || 'sum_checkbox_inputs',
      sourceFieldId: options.sourceFieldId || '',
      ...options
    }
  };
};

const initializeMultiRowFieldOptions = (field: FormField): FormField => {
  if (!field.options || typeof field.options !== 'object') {
    return {
      ...field,
      options: {
        allowAddRows: true,
        initialRowCount: 3,
        rowLabels: [],
        columns: []
      }
    };
  }

  const options = field.options as any;

  return {
    ...field,
    options: {
      allowAddRows: options.allowAddRows ?? true,
      initialRowCount: options.initialRowCount || 3,
      rowLabels: options.rowLabels || [],
      columns: options.columns || [],
      ...options
    }
  };
};

const initializeAVCheckFieldOptions = (field: FormField): FormField => {
  const options = (field.options && typeof field.options === 'object' ? field.options : {}) as any;
  return {
    ...field,
    options: {
      requireBoth: options.requireBoth ?? true
    }
  };
};

const initializeConnectionCheckFieldOptions = (field: FormField): FormField => {
  const options = (field.options && typeof field.options === 'object' ? field.options : {}) as any;
  return {
    ...field,
    options: {
      minDownMbps: options.minDownMbps ?? 3,
      minUpMbps: options.minUpMbps ?? 2,
      maxRttMs: options.maxRttMs ?? 250
    }
  };
};

const initializeSchedulerFieldOptions = (field: FormField): FormField => {
  const options = (field.options && typeof field.options === 'object' ? field.options : {}) as any;
  return {
    ...field,
    options: {
      calLink: options.calLink || ''
    }
  };
};